import type {
  ChannelRateAvailabilityPush,
  ChannelReservationImportRecord,
  ChannelSyncResult,
} from "./types.ts";

export interface ChannelProvider {
  readonly providerCode: string;
  readonly displayName: string;
  pullReservations(): Promise<ChannelReservationImportRecord[]>;
  pushRatesAndAvailability(payload: ChannelRateAvailabilityPush): Promise<ChannelSyncResult>;
}

export type MockChannelProvider = ChannelProvider & {
  pulledReservations: ChannelReservationImportRecord[];
  pushedPayloads: ChannelRateAvailabilityPush[];
};

export function createMockChannelProvider(
  providerCode: string,
  displayName: string,
  reservationQueue: ChannelReservationImportRecord[] = [],
  behavior: { delayNextSync?: boolean; failNextSync?: boolean } = {}
): MockChannelProvider {
  const queue = [...reservationQueue];
  const mutableBehavior = { ...behavior };
  const pulledReservations: ChannelReservationImportRecord[] = [];
  const pushedPayloads: ChannelRateAvailabilityPush[] = [];

  return {
    providerCode,
    displayName,
    pulledReservations,
    pushedPayloads,
    async pullReservations() {
      const items = [...queue];
      pulledReservations.push(...items);
      queue.length = 0;
      return items;
    },
    async pushRatesAndAvailability(payload: ChannelRateAvailabilityPush) {
      pushedPayloads.push(payload);
      if (mutableBehavior.failNextSync) {
        mutableBehavior.failNextSync = false;
        throw new Error(`${displayName} sync failed.`);
      }
      if (mutableBehavior.delayNextSync) {
        mutableBehavior.delayNextSync = false;
        return {
          status: "pending" as const,
          remoteReference: `${providerCode}-delayed-${pushedPayloads.length}`,
          delayed: true,
        };
      }
      return {
        status: "success" as const,
        remoteReference: `${providerCode}-sync-${pushedPayloads.length}`,
      };
    },
  };
}
